import { Restaurant } from '../../../models';

/** 参与双地址复制、同步和差异提示的固定字段对。 */
export const RESTAURANT_ADDRESS_PAIRS = [
    { business: 'local', tax: 'reg_local', label: 'archive_form_address_diff_street' },
    { business: 'postcode', tax: 'reg_postcode', label: 'archive_form_address_diff_postcode' },
    { business: 'city', tax: 'reg_city', label: 'archive_form_address_diff_city' },
    { business: 'state', tax: 'reg_state', label: 'archive_form_address_diff_state' },
] as const;

/** 双地址整组复制方向。 */
export type RestaurantAddressCopyDirection = 'BUSINESS_TO_TAX' | 'TAX_TO_BUSINESS';

/** 可触发实时同步的营业地址字段。 */
export type RestaurantBusinessAddressKey = typeof RESTAURANT_ADDRESS_PAIRS[number]['business'];

/** 双地址字段对类型，供差异汇总直接展示字段标签。 */
export type RestaurantAddressPair = typeof RESTAURANT_ADDRESS_PAIRS[number];

/**
 * 判断营业地址与税务地址的四个同步字段是否相同。
 * @param data 餐厅表单数据
 * @returns 四组字段均相同时返回 true，空值按空字符串比较
 */
export function inferRestaurantAddressesSame(data: Partial<Restaurant>): boolean {
    return RESTAURANT_ADDRESS_PAIRS.every(({ business, tax }) => (
        (data[business] ?? '') === (data[tax] ?? '')
    ));
}

/**
 * 按方向复制四组地址字段，并保留不参与复制的字段。
 * @param data 餐厅表单数据
 * @param direction 地址复制方向
 * @returns 复制后的新餐厅数据
 */
export function copyRestaurantAddress(
    data: Partial<Restaurant>,
    direction: RestaurantAddressCopyDirection,
): Restaurant {
    /** 地址复制结果，避免直接修改调用方数据。 */
    const copiedData: Restaurant = { ...data };
    RESTAURANT_ADDRESS_PAIRS.forEach(({ business, tax }) => {
        if (direction === 'BUSINESS_TO_TAX') {
            copiedData[tax] = data[business];
            return;
        }
        copiedData[business] = data[tax];
    });
    return copiedData;
}

/**
 * OCR 填充后、开启「两个地址相同」前准备表单。
 * 若营业四项皆空而税务侧有值（如 Visura 只识别 sede legale），先税务→营业，
 * 避免随后营业→税务把识别值冲成空。
 * @param data 已写入 OCR 值的餐厅表单
 * @returns 可安全调用 enableRestaurantAddressSync 的新表单数据
 */
export function prepareRestaurantAddressForSameAddrSync(
    data: Partial<Restaurant>,
): Restaurant {
    /** 营业侧四个同步字段是否全部为空。 */
    const businessEmpty = RESTAURANT_ADDRESS_PAIRS.every(({ business }) => (
        String(data[business] ?? '') === ''
    ));
    /** 税务侧是否至少有一项非空识别值。 */
    const taxHasValue = RESTAURANT_ADDRESS_PAIRS.some(({ tax }) => (
        String(data[tax] ?? '') !== ''
    ));
    if (businessEmpty && taxHasValue) {
        return copyRestaurantAddress(data, 'TAX_TO_BUSINESS');
    }
    return { ...data };
}

/**
 * 开启双地址同步，暂存税务地址后用营业地址覆盖对应四项。
 * @param data 当前餐厅表单数据
 * @returns 同步后的数据及仅供前端会话使用的税务地址快照
 */
export function enableRestaurantAddressSync(data: Partial<Restaurant>): {
    /** 已同步的餐厅表单数据。 */
    data: Restaurant;
    /** 开启同步前的税务地址快照。 */
    stash: Partial<Restaurant>;
} {
    /** 税务地址会话快照，用于取消同步时还原。 */
    const stash: Partial<Restaurant> = {};
    RESTAURANT_ADDRESS_PAIRS.forEach(({ tax }) => {
        stash[tax] = data[tax];
    });
    return {
        data: copyRestaurantAddress(data, 'BUSINESS_TO_TAX'),
        stash,
    };
}

/**
 * 关闭双地址同步并还原此前暂存的税务地址。
 * @param data 当前餐厅表单数据
 * @param stash 开启同步前保存的税务地址快照
 * @returns 已还原税务地址的新餐厅数据
 */
export function disableRestaurantAddressSync(
    data: Partial<Restaurant>,
    stash: Partial<Restaurant>,
): Restaurant {
    /** 税务地址还原结果，避免直接修改当前表单数据。 */
    const restoredData: Restaurant = { ...data };
    RESTAURANT_ADDRESS_PAIRS.forEach(({ tax }) => {
        restoredData[tax] = stash[tax];
    });
    return restoredData;
}

/**
 * 将一个营业地址字段同步到对应税务地址字段。
 * @param data 当前餐厅表单数据
 * @param key 已更新的营业地址字段
 * @returns 已同步对应税务字段的新餐厅数据
 */
export function syncRestaurantAddressField(
    data: Partial<Restaurant>,
    key: RestaurantBusinessAddressKey,
): Restaurant {
    /** 当前营业字段对应的税务字段配置。 */
    const addressPair = RESTAURANT_ADDRESS_PAIRS.find(({ business }) => business === key);
    if (!addressPair) {
        return { ...data };
    }
    return {
        ...data,
        [addressPair.tax]: data[key],
    };
}

/**
 * 获取营业地址与税务地址中值不一致的字段对。
 * @param data 当前餐厅表单数据
 * @returns 不一致字段对列表，顺序与界面行顺序一致
 */
export function getRestaurantAddressDifferences(data: Partial<Restaurant>): RestaurantAddressPair[] {
    return RESTAURANT_ADDRESS_PAIRS.filter(({ business, tax }) => (
        (data[business] ?? '') !== (data[tax] ?? '')
    ));
}
